import { useQuery } from "@tanstack/react-query";
import { api } from "../data";
import { t, ta } from "./sprache";

/**
 * Eine Zeitzone je betrachtender Person (Befund 12): eigene Wahl im Profil (user_preferences.time_zone, 0041), sonst die
 * Zone des Browsers, sonst die der Agentur. Kalender, Chat und Glocke rechnen alle in diese Zone; wo sie von der Zone der
 * Agentur oder eines Termins abweicht, steht die Zone dabei.
 */

export function isTimeZone(zone: string | null | undefined): zone is string {
  if (!zone) return false;
  try {
    new Intl.DateTimeFormat("en-GB", { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

export function browserTimeZone(): string | null {
  try {
    const z = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return isTimeZone(z) ? z : null;
  } catch {
    return null;
  }
}

export function viewerZone(preference: string | null | undefined, fallback: string): string {
  if (isTimeZone(preference)) return preference;
  return browserTimeZone() ?? fallback;
}

/** Zone der angemeldeten Person; lädt die eigene Einstellung einmal (5 Minuten gültig). */
export function useViewerZone(fallback: string): string {
  const userId = api.session()?.userId ?? null;
  const q = useQuery({
    queryKey: ["user_preferences", userId, "zeitzone"],
    enabled: !!userId,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const rows = await api.select<{ time_zone: string | null }>("user_preferences", { columns: "time_zone", filters: [["user_id", "eq", userId!]] }).catch(() => []);
      return rows[0]?.time_zone ?? null;
    },
  });
  return viewerZone(q.data, fallback);
}

/** Kurzer Hinweis, wenn die Zone der Person von der der Agentur abweicht („Zeiten in deiner Zeitzone Europe/Lisbon“). */
export function zoneHint(viewer: string, agency: string): string | null {
  return viewer !== agency ? ta(`Zeiten in deiner Zeitzone (${viewer}); die Agentur arbeitet in ${agency}.`, `Times in your time zone (${viewer}); the agency works in ${agency}.`, `Zeiten in deiner Zeitzone (${viewer}); dein Konto ist auf ${agency} eingestellt.`, `Times in your time zone (${viewer}); your account is set to ${agency}.`) : null;
}
